import { Link, useNavigate } from "react-router-dom";
import { Trash2, Minus, Plus, ShoppingBag, ArrowRight } from "lucide-react";
import { useCart } from "@/context/CartContext";
import { formatBRL } from "@/lib/api";

export default function Cart() {
  const { items, updateQty, removeItem, total, lineKey } = useCart();
  const navigate = useNavigate();

  if (items.length === 0) {
    return (
      <div className="max-w-[1400px] mx-auto px-5 md:px-10 py-24 text-center">
        <ShoppingBag size={48} strokeWidth={1} className="mx-auto text-[#1A1A1A]/30" />
        <h1 className="font-serif text-4xl text-[#1A1A1A] mt-6">Sua sacola está vazia</h1>
        <p className="text-[#1A1A1A]/60 mt-3">Descubra peças que combinam com você.</p>
        <Link to="/loja" data-testid="empty-cart-shop-btn" className="inline-flex items-center gap-3 bg-[#1A1A1A] text-[#F8F7F4] px-8 py-4 mt-8 hover:bg-[#B89C65] transition-colors">
          <span className="overline">Explorar a loja</span> <ArrowRight size={16} />
        </Link>
      </div>
    );
  }

  return (
    <div className="max-w-[1400px] mx-auto px-5 md:px-10 py-14">
      <h1 className="font-serif text-5xl text-[#1A1A1A] mb-10">Sua sacola</h1>
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
        <div className="lg:col-span-2 divide-y divide-[#1A1A1A]/10">
          {items.map((item) => {
            const k = lineKey(item);
            return (
              <div key={k} data-testid={`cart-item-${item.product_id}`} className="flex gap-5 py-6">
                <div className="w-24 h-32 bg-[#EFEEEA] overflow-hidden flex-shrink-0">
                  <img src={item.image} alt={item.name} className="w-full h-full object-cover" />
                </div>
                <div className="flex-1 flex flex-col">
                  <div className="flex justify-between gap-4">
                    <div>
                      <h3 className="font-serif text-xl text-[#1A1A1A]">{item.name}</h3>
                      <p className="text-sm text-[#1A1A1A]/50 mt-1">
                        {[item.color, item.size].filter(Boolean).join(" · ")}
                      </p>
                    </div>
                    <button data-testid={`remove-${item.product_id}`} onClick={() => removeItem(k)} className="text-[#1A1A1A]/40 hover:text-[#B89C65] transition-colors h-fit">
                      <Trash2 size={18} />
                    </button>
                  </div>
                  <div className="flex items-center justify-between mt-auto pt-4">
                    <div className="flex items-center border border-[#1A1A1A]/20">
                      <button onClick={() => updateQty(k, item.quantity - 1)} className="p-2 hover:text-[#B89C65] transition-colors"><Minus size={14} /></button>
                      <span className="w-8 text-center text-sm">{item.quantity}</span>
                      <button onClick={() => updateQty(k, item.quantity + 1)} className="p-2 hover:text-[#B89C65] transition-colors"><Plus size={14} /></button>
                    </div>
                    <span className="text-[#1A1A1A]">{formatBRL(item.price * item.quantity)}</span>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        <div className="lg:col-span-1">
          <div className="bg-[#FFFFFF] border border-[#1A1A1A]/10 p-8 sticky top-28">
            <h2 className="font-serif text-2xl text-[#1A1A1A] mb-6">Resumo</h2>
            <div className="flex justify-between text-[#1A1A1A]/70 mb-3">
              <span>Subtotal</span><span data-testid="cart-subtotal">{formatBRL(total)}</span>
            </div>
            <div className="flex justify-between text-[#1A1A1A]/70 mb-3">
              <span>Frete</span><span>Calculado no checkout</span>
            </div>
            <div className="h-px bg-[#1A1A1A]/10 my-4" />
            <div className="flex justify-between text-[#1A1A1A] text-lg font-medium mb-6">
              <span>Total</span><span data-testid="cart-total">{formatBRL(total)}</span>
            </div>
            <button data-testid="checkout-btn" onClick={() => navigate("/checkout")}
              className="w-full flex items-center justify-center gap-3 bg-[#1A1A1A] text-[#F8F7F4] py-4 hover:bg-[#B89C65] transition-colors">
              <span className="overline">Finalizar compra</span> <ArrowRight size={16} />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
